import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, CheckCircle } from '@phosphor-icons/react';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { isBabyPatient } from '@/lib/utils';
import { Scale0to10 } from '@/components/ConsultationFeedback';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FeedbackConsultaPage() {
  const { consultationId } = useParams();
  const { user } = useAuth();
  const isSuperBebes = isBabyPatient(user);
  const [consultation, setConsultation] = useState(null);
  const [rating, setRating] = useState(0);
  const [doubts, setDoubts] = useState(null); // null | true | false
  const [comment, setComment] = useState('');
  const [planSpeed, setPlanSpeed] = useState(5);
  const [techQuality, setTechQuality] = useState(5);
  const [materialsUseful, setMaterialsUseful] = useState(5);
  const [menuOpinion, setMenuOpinion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/my/consultations/${consultationId}`);
        setConsultation(data);
      } catch {
        toast.error('Consulta não encontrada');
      }
    })();
  }, [consultationId]);

  const submit = async () => {
    if (!rating) { toast.error('Escolhe uma nota'); return; }
    if (doubts === null) { toast.error('Indica se ficaste com dúvidas'); return; }
    setSubmitting(true);
    try {
      const payload = { consultation_id: consultationId, rating, doubts, comment };
      if (isSuperBebes) {
        payload.menu_opinion = menuOpinion;
      } else {
        payload.plan_speed = planSpeed;
        payload.tech_quality = techQuality;
        payload.materials_useful = materialsUseful;
      }
      await axios.post(`${API}/api/consultation-feedback`, payload);
      setDone(true);
    } catch {
      toast.error('Não foi possível enviar');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="fade-in max-w-md mx-auto text-center py-10" data-testid="feedback-done">
        <CheckCircle size={48} className="mx-auto text-brand-green mb-3" weight="duotone" />
        <h1 className="font-heading text-2xl text-brand-text mb-2">Obrigada pelo feedback!</h1>
        <p className="text-sm text-brand-muted mb-6">Ajuda-nos a melhorar o teu acompanhamento.</p>
        <Link to="/dashboard" className="inline-block bg-brand-green text-white rounded-full px-5 py-2 text-sm" data-testid="back-to-dashboard">Voltar ao Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="fade-in max-w-md mx-auto" data-testid="feedback-page">
      <h1 className="font-heading text-2xl text-brand-text mb-1">Como correu a consulta?</h1>
      <p className="text-xs text-brand-muted mb-5">{consultation?.date ? `Consulta de ${consultation.date}` : 'Feedback rápido (30 segundos)'}</p>

      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-3">
        <p className="text-xs text-brand-muted mb-2">Que nota dás à consulta?</p>
        <div className="flex gap-2 justify-center" data-testid="rating-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => setRating(n)}
              className="p-1"
              data-testid={`rating-${n}`}
              aria-label={`Nota ${n}`}
            >
              <Star size={32} weight={n <= rating ? 'fill' : 'regular'} className={n <= rating ? 'text-brand-gold' : 'text-brand-placeholder'} />
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-3">
        <p className="text-xs text-brand-muted mb-3">Ficaste com dúvidas por esclarecer?</p>
        <div className="flex gap-2">
          <button
            onClick={() => setDoubts(false)}
            className={`flex-1 py-2 rounded-xl text-xs font-medium ${doubts === false ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}
            data-testid="doubts-no"
          >Não</button>
          <button
            onClick={() => setDoubts(true)}
            className={`flex-1 py-2 rounded-xl text-xs font-medium ${doubts === true ? 'bg-brand-gold text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}
            data-testid="doubts-yes"
          >Sim, gostava de falar</button>
        </div>
      </div>

      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-3">
        {isSuperBebes ? (
          <div data-testid="sb-question">
            <p className="text-xs text-brand-muted mb-2">O que achas da ementa proposta para o teu bebé?</p>
            <Textarea value={menuOpinion} onChange={e => setMenuOpinion(e.target.value)} placeholder="Partilha a tua opinião..." className="rounded-xl border-brand-border text-sm min-h-[70px]" data-testid="feedback-menu-opinion" />
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <p className="text-xs text-brand-muted mb-1">Rapidez de entrega do plano alimentar? <span className="text-brand-placeholder">(0–10)</span></p>
              <Scale0to10 value={planSpeed} onChange={setPlanSpeed} testid="feedback-plan-speed" />
            </div>
            <div>
              <p className="text-xs text-brand-muted mb-1">Qualidade das respostas técnicas e científicas? <span className="text-brand-placeholder">(0–10)</span></p>
              <Scale0to10 value={techQuality} onChange={setTechQuality} testid="feedback-tech-quality" />
            </div>
            <div>
              <p className="text-xs text-brand-muted mb-1">Os materiais de apoio foram úteis? <span className="text-brand-placeholder">(0–10)</span></p>
              <Scale0to10 value={materialsUseful} onChange={setMaterialsUseful} testid="feedback-materials-useful" />
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4">
        <p className="text-xs text-brand-muted mb-2">Queres acrescentar algo? (opcional)</p>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Ex.: gostei da forma como explicaste..."
          className="rounded-xl border-brand-border text-sm min-h-[80px]"
          data-testid="comment-input"
        />
      </div>

      <button
        onClick={submit}
        disabled={submitting}
        className="w-full bg-brand-green text-white rounded-full py-3 text-sm font-medium disabled:opacity-50"
        data-testid="submit-feedback"
      >
        {submitting ? 'A enviar...' : 'Enviar feedback'}
      </button>
    </div>
  );
}
